import type {
	DehydratedState,
	VueQueryPluginOptions,
} from '@tanstack/vue-query';
import {
	VueQueryPlugin,
	QueryClient,
	hydrate,
	dehydrate,
	QueryCache,
	MutationCache,
} from '@tanstack/vue-query';
import { isAuthError } from '@supabase/supabase-js';
import { defineNuxtPlugin, useState } from '#imports';
import { isNetworkError } from '~~/types/NetworkError';
import { reportRequestFailure } from '~/utils/reportRequestFailure';
import { useSupabaseClient } from '~/composables/useSupabaseClient';

export default defineNuxtPlugin({
	name: 'vue-query',
	setup: (nuxt) => {
		const vueQueryState = useState<DehydratedState | null>('vue-query');

		const queryCache = new QueryCache({
			onError: (error, query) => {
				if (query.meta?.ignoreSentry) {
					return;
				}

				reportRequestFailure({
					error,
					source: 'query',
					key: String(query.queryKey[0]),
					lastSuccessAt: query.state.dataUpdatedAt,
					tags: { queryKey: query.queryKey.join(',') },
					contexts: { query: { meta: query.meta, state: query.state } },
				});
			},
		});

		const mutationCache = new MutationCache({
			onError: (error, variables, context, mutation) => {
				if (mutation.meta?.ignoreSentry) {
					return;
				}

				reportRequestFailure({
					error,
					source: 'mutation',
					key: String(mutation.options.mutationKey?.[0] ?? 'unknown-mutation'),
					lastSuccessAt: null,
					tags: { mutationKey: mutation.options.mutationKey?.join(',') ?? 'unknown-mutation' },
					contexts: { mutation: { variables } },
				});
			},
		});

		const queryClient = new QueryClient({
			defaultOptions: {
				queries: {
					staleTime: 60 * 1000,
					// auth-js caches a failed refresh for 60s, so retrying inside the query window cannot help.
					retry: (failureCount, error) => {
						if (isNetworkError(error) && isAuthError(error.cause)) {
							return false;
						}

						return isNetworkError(error) ? failureCount < 3 : failureCount < 1;
					},
					refetchOnWindowFocus: false,
				},
			},
			queryCache,
			mutationCache,
		});

		const options: VueQueryPluginOptions = { queryClient };

		nuxt.vueApp.use(VueQueryPlugin, options);

		if (import.meta.server) {
			nuxt.hooks.hook('app:rendered', () => {
				vueQueryState.value = dehydrate(queryClient);
			});
		}

		if (import.meta.client) {
			nuxt.hooks.hook('app:created', () => {
				// Only set after a server render – with ssr: false it stays empty, and hydrate() throws on that.
				if (vueQueryState.value) {
					hydrate(queryClient, vueQueryState.value);
				}
			});
		}

		const supabase = useSupabaseClient();

		supabase.auth.onAuthStateChange((state) => {
			if (state === 'SIGNED_OUT') {
				queryClient.clear();
			}
		});
	},
});
